/**
 * Módulo de utilitários para interagir com o Local Storage do navegador.
 * Fornece funções seguras e tipadas para salvar, obter e remover itens,
 * com tratamento de erros robusto para garantir uma experiência de usuário impecável
 * e um comportamento previsível da aplicação.
 */

/**
 * Salva um item no Local Storage do navegador.
 *
 * Esta função tenta armazenar um par chave-valor no Local Storage.
 * Em caso de falha (por exemplo, quota excedida ou configurações de privacidade),
 * um erro é capturado e logado no console, sem interromper a execução da aplicação.
 *
 * @param chave A chave única para identificar o item no Local Storage.
 * @param valor O valor a ser salvo, que deve ser uma string.
 */
export const salvarItem = (chave: string, valor: string): void => {
  try {
    localStorage.setItem(chave, valor);
  } catch (error) {
    console.error(`Erro ao salvar o item '${chave}' no Local Storage:`, error);
    // Poderia ser integrado com um serviço de log de erros ou notificação ao usuário
  }
};

/**
 * Obtém um item do Local Storage do navegador.
 *
 * Esta função tenta recuperar um item do Local Storage pela sua chave.
 * Se o item não for encontrado ou ocorrer um erro durante a leitura,
 * a função retorna `null` e um erro é logado no console, garantindo
 * que a aplicação possa lidar com a ausência de dados de forma elegante.
 *
 * @param chave A chave do item a ser obtido do Local Storage.
 * @returns O valor do item como string, ou `null` se o item não existir ou ocorrer um erro.
 */
export const obterItem = (chave: string): string | null => {
  try {
    return localStorage.getItem(chave);
  } catch (error) {
    console.error(`Erro ao obter o item '${chave}' do Local Storage:`, error);
    return null;
  }
};

/**
 * Remove um item do Local Storage do navegador.
 *
 * Esta função tenta remover um item do Local Storage pela sua chave.
 * Em caso de falha, um erro é capturado e logado no console,
 * assegurando que a operação não cause falhas inesperadas na aplicação.
 *
 * @param chave A chave do item a ser removido do Local Storage.
 */
export const removerItem = (chave: string): void => {
  try {
    localStorage.removeItem(chave);
  } catch (error) {
    console.error(`Erro ao remover o item '${chave}' do Local Storage:`, error);
    // Poderia ser integrado com um serviço de log de erros ou notificação ao usuário
  }
};